// src/components/AIRecommendations.js
import { useState, useEffect } from "react";
import { GROQ_API_KEY, GROQ_MODEL } from "../config";
import { CAT_ICONS } from "../constants";

async function callGroq(prompt) {
  const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      max_tokens: 600,
      temperature: 0.7,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error.message);
  return data.choices?.[0]?.message?.content || "";
}

export default function AIRecommendations({ task, onAddTask, onDismiss }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    generateRecommendations();
  }, [task.id]);

  async function generateRecommendations() {
    setLoading(true);
    try {
      const raw = await callGroq(
        `A homeowner just completed this home repair task: "${task.title}" (Category: ${task.category}).
${task.notes ? `Task description: ${task.notes}` : ""}

Suggest 3 follow-up tasks they should consider doing next. Think practically — what naturally comes after this repair? For example after fixing a leaky faucet, suggest checking pipes under sink, testing water pressure, etc.

Return ONLY a valid JSON array. No markdown, no explanation. Each element:
{
  "title": "Task title",
  "category": "One of: Plumbing, Electrical, HVAC, Structural, Roofing, Flooring, Appliances, Exterior, Interior, Landscaping, Other",
  "reason": "One sentence explaining why this is a good follow-up",
  "suggestedDays": 7
}

Return exactly 3 suggestions.`
      );
      const cleaned = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      setRecommendations(parsed);
    } catch (err) {
      setError("Couldn't generate recommendations right now.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(rec, index) {
    setAdding(index);
    // Calculate suggested due date
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (rec.suggestedDays || 7));
    const dueDateStr = dueDate.toISOString().split("T")[0];

    await onAddTask({
      title: rec.title,
      category: rec.category || "Other",
      priority: "Medium",
      notes: `Recommended after completing: ${task.title}`,
      dueDate: dueDateStr,
      recurrence: "None",
      completed: false,
      images: [],
      partsList: [],
      repairGuide: "",
    });

    setAdded(prev => [...prev, index]);
    setAdding(null);
  }

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
          <h2 style={styles.title}>Task Complete!</h2>
          <p style={styles.subtitle}>
            Great work on <strong>{task.title}</strong>! Based on what you just fixed, here are some follow-up tasks you might want to add:
          </p>
        </div>

        {/* Recommendations */}
        <div style={styles.list}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 24, color: "#a07850" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
              <p style={{ fontWeight: 700, fontSize: 14 }}>Finding related tasks...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: 16, color: "#ef4444", fontWeight: 700, fontSize: 13 }}>{error}</div>
          ) : (
            recommendations.map((rec, i) => (
              <div key={i} style={{ ...styles.recCard, opacity: added.includes(i) ? 0.6 : 1 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{CAT_ICONS[rec.category] || "🔨"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#2c1810", marginBottom: 3 }}>{rec.title}</div>
                    <div style={{ fontSize: 12, color: "#a07850", fontWeight: 600, lineHeight: 1.5 }}>{rec.reason}</div>
                    <div style={{ fontSize: 11, color: "#c09870", fontWeight: 700, marginTop: 4 }}>
                      Suggested: within {rec.suggestedDays || 7} days
                    </div>
                  </div>
                  <button
                    onClick={() => handleAdd(rec, i)}
                    disabled={adding === i || added.includes(i)}
                    style={{
                      ...styles.addBtn,
                      background: added.includes(i) ? "#f0fdf4" : "linear-gradient(135deg, #ff8c00, #f97316)",
                      color: added.includes(i) ? "#22c55e" : "white",
                      border: added.includes(i) ? "2px solid #86efac" : "none",
                    }}
                  >
                    {adding === i ? "..." : added.includes(i) ? "✓ Added" : "+ Add"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Actions */}
        <div style={styles.footer}>
          <button className="btn-secondary" style={{ width: "100%" }} onClick={onDismiss}>
            {added.length > 0 ? `Done (${added.length} task${added.length !== 1 ? "s" : ""} added)` : "No thanks, dismiss"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
    zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, animation: "fadeIn 0.2s ease",
  },
  modal: {
    background: "white", borderRadius: 24, width: "100%", maxWidth: 480,
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
    maxHeight: "90vh", display: "flex", flexDirection: "column",
  },
  header: {
    background: "linear-gradient(135deg, #fff7ed, #fef3c7)",
    padding: "28px 28px 20px", textAlign: "center",
    borderBottom: "2px solid #f0d8b0",
  },
  title: {
    fontFamily: "'Fredoka One', cursive", fontSize: 26, color: "#8B4513", marginBottom: 8,
  },
  subtitle: {
    fontSize: 13, color: "#a07850", fontWeight: 600, lineHeight: 1.6,
  },
  list: {
    padding: "16px 20px", overflowY: "auto", flex: 1,
  },
  recCard: {
    background: "#fffaf4", border: "2px solid #f0d8b0", borderRadius: 14,
    padding: "14px 16px", marginBottom: 10, transition: "all 0.18s",
  },
  addBtn: {
    padding: "8px 14px", borderRadius: 10, cursor: "pointer",
    fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800,
    flexShrink: 0, transition: "all 0.18s", whiteSpace: "nowrap",
  },
  footer: {
    padding: "16px 20px", borderTop: "2px solid #f0d8b0",
  },
};
